import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Briefcase, 
  MapPin, 
  Clock, 
  Rocket, 
  Users, 
  Heart, 
  Coffee, 
  Globe, 
  ShieldCheck,
  Code,
  Layout,
  Database,
  Sparkles,
  Zap,
  CheckCircle2,
  Search,
  MessageSquare,
  FileText,
  Building2,
  Cpu,
  Trophy,
  History,
  BookOpen,
  HelpCircle,
  Award,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Terminal,
  Layers,
  Cloud,
  Eye,
  HandMetal
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const values = [
  {
    title: "Radical Transparency",
    desc: "Following Leanport's philosophy, we build in public and share everything from our roadmap to decision-making.",
    icon: Search,
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    title: "Security by Default",
    desc: "Trust is our most valuable asset. We prioritize security in every line of code, backed by a decade of engineering excellence.",
    icon: ShieldCheck,
    color: "bg-green-50",
    iconColor: "text-green-600"
  },
  {
    title: "Velocity with Purpose",
    desc: "We move fast, but we move together. We value deliberate action and high-quality offshore delivery standards.",
    icon: Zap,
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  },
  {
    title: "Human-Centric Design",
    desc: "Technology should serve people. Our mission is to empower businesses by handling their complex tech needs.",
    icon: Users,
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  }
];

const techStack = [
  { name: "React", category: "Frontend", icon: Layout },
  { name: "Node.js", category: "Backend", icon: Terminal },
  { name: "Gemini AI", category: "Intelligence", icon: Sparkles },
  { name: "MongoDB", category: "Database", icon: Database },
  { name: "Tailwind CSS", category: "Styling", icon: Layers },
  { name: "Salesforce", category: "Integration", icon: Cloud },
  { name: "Docker", category: "DevOps", icon: Building2 },
  { name: "Socket.io", category: "Real-time", icon: Zap }
];

const journey = [
  { year: "2011", title: "Foundation", desc: "Leanport Software was founded in Noida with a vision to transform digital engineering." },
  { year: "2015", title: "Global Expansion", desc: "Established our presence in the international market, providing high-end offshore services." },
  { year: "2020", title: "PMS Prototype", desc: "Initiated development of PMS to solve project management complexities we faced internally." },
  { year: "2024", title: "AI Revolution", desc: "Integrated Gemini AI into PMS, setting new standards for intelligent task automation." }
];

const growthPrograms = [
  {
    title: "Mentorship Program",
    desc: "Get paired with senior leads to accelerate your career path and technical mastery.",
    icon: Users
  },
  {
    title: "Learning Budget",
    desc: "Annual budget for courses, books, and international tech conferences to help you grow.",
    icon: BookOpen
  },
  {
    title: "Internal Workshops",
    desc: "Weekly knowledge-sharing sessions on AI, Salesforce, and modern web architecture.",
    icon: Cpu
  },
  {
    title: "Clear Career Ladders",
    desc: "Transparent promotion paths from Junior to Principal Engineer and beyond.",
    icon: Award
  }
];

const faqs = [
  {
    q: "What is the working model at PMS?",
    a: "We are remote-first and async-friendly. While we have delivery centers in Noida and New Delhi, we empower our team to work from where they are most productive."
  },
  {
    q: "What technology stack do you use?",
    a: "Our core stack includes React, Node.js, and MongoDB. We also leverage Gemini AI for intelligent features and have deep expertise in Salesforce integrations."
  },
  {
    q: "Do you provide equipment for remote work?",
    a: "Yes, we provide a high-end laptop and a generous stipend to set up your home office (ergonomic chair, monitor, etc.)."
  },
  {
    q: "What does the onboarding process look like?",
    a: "You'll go through a 2-week structured onboarding where you'll meet the team, learn our engineering standards, and get hands-on with TeamBoard."
  }
];

const expertise = [
  {
    title: "Full-Stack Development",
    desc: "Building scalable web and mobile applications using modern frameworks like React, Node.js, and Salesforce.",
    icon: Code,
  },
  {
    title: "AI & Automation",
    desc: "Integrating Gemini AI to transform manual project workflows into intelligent, automated processes.",
    icon: Cpu,
  },
  {
    title: "Digital Engineering",
    desc: "Delivering high-end consultancy and offshore development services with a global reach since 2011.",
    icon: Globe,
  },
  {
    title: "Secure Infrastructure",
    desc: "Specializing in encrypted credentials management and role-based access control for enterprise clients.",
    icon: ShieldCheck,
  }
];

const benefits = [
  {
    title: "Remote-First",
    desc: "Work from anywhere. We provide a stipend to set up your dream home office, supported by our Noida delivery center.",
    icon: Globe,
    color: "bg-sky",
    textColor: "text-navy"
  },
  {
    title: "Flexible Hours",
    desc: "We care about results, not clocking in. Manage your time to fit your life and personal growth.",
    icon: Clock,
    color: "bg-sky",
    textColor: "text-navy"
  },
  {
    title: "Health & Care",
    desc: "Premium insurance coverage and mental health support for you and your family.",
    icon: Heart,
    color: "bg-sky",
    textColor: "text-navy"
  },
  {
    title: "Equity & Growth",
    desc: "Stock options for every employee and a dedicated budget for professional certifications.",
    icon: Rocket,
    color: "bg-sky",
    textColor: "text-navy"
  }
];

const hiringSteps = [
  {
    title: "Application",
    desc: "Submit your resume and portfolio. We review every application personally at our recruitment hub.",
    icon: FileText
  },
  {
    title: "Intro Call",
    desc: "A 30-minute chat to align on values, Leanport's vision, and role fit.",
    icon: MessageSquare
  },
  {
    title: "Technical Review",
    desc: "Showcase your skills through a collaborative session or a take-home project.",
    icon: Code
  },
  {
    title: "Final Interview",
    desc: "Meet the leadership and dive deeper into the vision and your future impact.",
    icon: Users
  }
];

const openPositions = [
  {
    title: "Senior Full Stack Engineer",
    department: "Engineering",
    location: "Remote / Noida",
    type: "Full-time",
    icon: Code,
    tag: "High Priority"
  },
  {
    title: "Product Designer",
    department: "Design",
    location: "Remote / New Delhi",
    type: "Full-time",
    icon: Layout,
    tag: "New"
  },
  {
    title: "Backend Engineer (Node.js)",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    icon: Database,
    tag: ""
  },
  {
    title: "Growth Marketer",
    department: "Marketing",
    location: "Remote",
    type: "Full-time",
    icon: Sparkles,
    tag: ""
  }
];

const leadership = [
  {
    name: "Nematullah",
    role: "Founder",
    bio: "Visionary leader with a passion for building scalable engineering teams and transformative digital products.",
    avatar: "N"
  },
  {
    name: "Yasir Mortaza",
    role: "Managing Director",
    bio: "Architect of Leanport's technical foundation, specializing in complex enterprise integrations and AI systems.",
    avatar: "YM"
  }
];

const cultureImages = [
  { title: "Noida Delivery Center", desc: "Our hub for innovation and collaborative engineering.", icon: Building2 },
  { title: "Team Hackathons", desc: "Pushing the boundaries of what's possible with AI.", icon: Cpu },
  { title: "Global Offsites", desc: "Connecting our remote-first team across borders.", icon: Globe },
  { title: "Knowledge Sharing", desc: "Weekly sessions to master new tech stacks.", icon: BookOpen }
];

const CareersPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="positions" ctaLabel="Join the Team" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-[85vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400 rounded-full blur-[120px] -mr-40 -mt-40" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 rounded-full blur-[100px] -ml-20 -mb-20" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="text-center lg:text-left reveal-left">
                <a href="#about-leanport" onClick={(e) => handleNavClick(e, "about-leanport")} className="inline-flex items-center gap-2 px-4 py-2 bg-navy/10 hover:bg-navy/20 border border-navy/10 rounded-full mb-8 transition-all group">
                  <span className="text-navy text-sm font-bold uppercase tracking-widest">Built by Leanport Software</span>
                  <ArrowRight className="w-4 h-4 text-navy group-hover:translate-x-1 transition-transform" />
                </a>
                <h1 className="text-navy mb-8">
                  Help us build the <span className="text-blue-600">operating system</span> for modern teams.
                </h1>
                <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl">
                  PMS is the flagship PMS product of Leanport Software Pvt. Ltd., redefining project management with AI and enterprise-grade security.
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                  <a href="#positions" onClick={(e) => handleNavClick(e, "positions")} className="btn-primary">
                    Browse Openings
                  </a>
                  <a href="#about-leanport" onClick={(e) => handleNavClick(e, "about-leanport")} className="btn-secondary !bg-white/50 !text-navy !border-navy/10 hover:!bg-white/80">
                    About the Company
                  </a>
                </div>
              </div>
              
              <div className="hidden lg:block relative reveal-right">
                <div className="relative bg-white/40 backdrop-blur-xl rounded-[40px] p-8 border border-white/60 shadow-2xl overflow-hidden">
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 bg-white/60 p-4 rounded-2xl border border-white/80">
                      <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">JD</div>
                      <div>
                        <p className="text-navy font-bold">Innovation Lead</p>
                        <p className="text-gray-600 text-sm">Aligning stakeholder goals since 2011</p>
                      </div>
                      <CheckCircle2 className="w-6 h-6 text-green-600 ml-auto" />
                    </div>
                    <div className="flex items-center gap-4 bg-white/60 p-4 rounded-2xl border border-white/80 opacity-80">
                      <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold">MK</div>
                      <div>
                        <p className="text-navy font-bold">Tech Excellence</p>
                        <p className="text-gray-600 text-sm">Empowering businesses globally</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 bg-navy text-white font-black px-6 py-4 rounded-2xl shadow-2xl transform rotate-3 animate-bounce-gentle">
                  Leanport Software
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Leanport Section */}
        <section id="about-leanport" className="shell-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">The Company Behind the Product</span>
                <h2 className="mt-4 mb-6 text-navy">Empowering businesses through <span className="text-blue-600">innovation.</span></h2>
                <div className="space-y-6 text-lg text-gray-600">
                  <p>
                    Founded in 2011, <span className="font-bold text-navy">Leanport Software Pvt. Ltd.</span> has been at the forefront of digital transformation for over a decade. Headquartered in Noida, we specialize in high-end consulting and offshore development services.
                  </p>
                  <p>
                    Our mission is simple: to empower businesses by handling their technological needs, allowing them to concentrate on their core activities. PMS is the culmination of our vision to align the goals of all stakeholders through intelligent technology.
                  </p>
                  <div className="flex flex-wrap gap-8 pt-4">
                    <div>
                      <p className="text-3xl font-black text-navy">13+</p>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Years Experience</p>
                    </div>
                    <div>
                      <p className="text-3xl font-black text-navy">Global</p>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Reach</p>
                    </div>
                    <div>
                      <p className="text-3xl font-black text-navy">500+</p>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Projects Delivered</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="reveal-right">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-8 rounded-3xl text-center">
                    <Building2 className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                    <h4 className="font-bold text-gray-900">Headquarters</h4>
                    <p className="text-sm text-gray-600">Noida, Uttar Pradesh</p>
                  </div>
                  <div className="bg-green-50 p-8 rounded-3xl text-center">
                    <Trophy className="w-10 h-10 text-green-600 mx-auto mb-4" />
                    <h4 className="font-bold text-gray-900">Expertise</h4>
                    <p className="text-sm text-gray-600">Full-Stack & AI</p>
                  </div>
                  <div className="bg-purple-50 p-8 rounded-3xl text-center col-span-2">
                    <Rocket className="w-10 h-10 text-purple-600 mx-auto mb-4" />
                    <h4 className="font-bold text-gray-900">Our Vision</h4>
                    <p className="text-sm text-gray-600">To be a globally inspiring company that aligns all stakeholders.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diversity & Inclusion Section */}
        <section className="shell-sky py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left order-2 lg:order-1">
                <div className="relative rounded-[40px] overflow-hidden shadow-2xl bg-navy p-12">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <HandMetal className="w-32 h-32 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-6">Built by Everyone, For Everyone.</h3>
                  <p className="text-blue-100 text-lg leading-relaxed mb-8">
                    At Leanport, we believe our engineering is strongest when it reflects the diversity of the world. We are committed to creating an environment where every voice is heard and every perspective is valued.
                  </p>
                  <div className="space-y-4">
                    {[
                      "Gender-neutral recruitment processes.",
                      "Inclusive culture that celebrates global holidays.",
                      "Equal opportunities for growth and leadership.",
                      "Active support for neurodiversity in engineering."
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-sky" />
                        <span className="text-blue-100">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="reveal-right order-1 lg:order-2">
                <span className="section-eyebrow">Equity at Work</span>
                <h2 className="mt-4 mb-6 text-navy">A culture of <span className="text-blue-600">belonging.</span></h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  We don't just tolerate differences; we thrive because of them. Our remote-first model allows us to tap into talent from all walks of life, across all time zones.
                </p>
                <div className="flex items-center gap-4 p-6 bg-white rounded-2xl border border-blue-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-navy text-lg italic">"Diversity is our competitive advantage."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Journey Timeline */}
        <section className="shell-white overflow-hidden">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">The Timeline</span>
              <h2 className="mt-4 mb-6 text-navy">Our <span className="text-blue-600">Journey.</span></h2>
            </div>
            
            <div className="relative">
              <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 hidden lg:block" />
              <div className="grid lg:grid-cols-4 gap-8">
                {journey.map((item, idx) => (
                  <div key={idx} className="relative bg-white p-8 rounded-[32px] border border-gray-50 shadow-sm reveal stagger-1">
                    <div className="w-12 h-12 p-8 bg-navy text-sky rounded-full flex items-center justify-center font-bold text-lg mb-6 relative z-10 lg:mx-auto">
                      {item.year}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-navy lg:text-center">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed lg:text-center">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="shell-white border-t border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">The Visionaries</span>
              <h2 className="mt-4 mb-6 text-navy">Our <span className="text-blue-600">Leadership.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Led by engineers who believe in building tools that empower others.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {leadership.map((member, idx) => (
                <div key={idx} className="group flex flex-col md:flex-row items-center gap-8 p-10 bg-gray-50 rounded-[40px] transition-all hover:bg-white hover:shadow-2xl reveal stagger-1">
                  <div className="w-32 h-32 rounded-3xl bg-navy text-sky flex items-center justify-center text-4xl font-black flex-shrink-0 group-hover:scale-105 transition-transform">
                    {member.avatar}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-bold text-navy mb-1">{member.name}</h3>
                    <p className="text-blue-600 font-bold text-sm uppercase tracking-widest mb-4">{member.role}</p>
                    <p className="text-gray-600 leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Modern Tech Stack Grid */}
        <section className="shell-navy py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow !bg-white/10 !text-white !border-white/20">The Engine Room</span>
              <h2 className="mt-4 text-white">Our Modern <span className="text-sky">Tech Stack.</span></h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mt-6">
                We use the best tools to build the best product. Here's what you'll be working with every day.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {techStack.map((tech, idx) => (
                <div key={idx} className="group p-8 bg-white/5 border border-white/10 rounded-3xl text-center hover:bg-white/10 transition-all reveal stagger-1">
                  <div className="w-16 h-16 bg-sky/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:bg-sky/20 transition-all">
                    <tech.icon className="w-8 h-8 text-sky" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">{tech.name}</h4>
                  <p className="text-sky/60 text-sm font-medium uppercase tracking-widest">{tech.category}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Expertise Section */}
        <section className="shell-white border-t border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Engineering Excellence</span>
              <h2 className="mt-4 text-navy">Our Technology <span className="text-blue-600">Expertise.</span></h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {expertise.map((item, idx) => (
                <div key={idx} className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm hover:shadow-xl transition-all reveal stagger-1">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Life at Leanport Gallery */}
        <section className="shell-sky">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Inside the Lab</span>
              <h2 className="mt-4 mb-6 text-navy">Life at <span className="text-blue-600">Leanport.</span></h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cultureImages.map((item, idx) => (
                <div key={idx} className="group relative bg-white p-8 rounded-[32px] shadow-sm hover:shadow-2xl transition-all reveal stagger-1 overflow-hidden text-center lg:text-left">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform mx-auto lg:mx-0">
                    <item.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-navy">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Sparkles className="w-20 h-20 text-navy" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Growth */}
        <section className="shell-white py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Growth & Development</span>
                <h2 className="mt-6 mb-8 text-navy">Invest in your <span className="text-blue-600">future.</span></h2>
                <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                  We don't just hire for what you know now. We hire for your potential. At Leanport, continuous learning is part of the job description.
                </p>
                <div className="grid sm:grid-cols-2 gap-8">
                  {growthPrograms.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-navy mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reveal-right hidden lg:block">
                <div className="relative p-8 bg-blue-50 border border-blue-100 rounded-[40px]">
                  <div className="bg-white rounded-3xl p-8 text-navy shadow-2xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-sky rounded-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-navy" />
                      </div>
                      <div>
                        <p className="font-bold">Latest Certification</p>
                        <p className="text-sm text-gray-500">Cloud Architecture</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 w-[85%]" />
                      </div>
                      <div className="flex justify-between text-sm font-bold">
                        <span>Course Progress</span>
                        <span>85%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Referral Program Section */}
        <section className="shell-sky py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-[40px] p-12 shadow-2xl border border-blue-100 relative overflow-hidden reveal">
              <div className="absolute -top-10 -right-10 opacity-5">
                <UserPlus className="w-64 h-64 text-blue-600" />
              </div>
              <div className="relative z-10 text-center">
                <span className="section-eyebrow">Spread the word</span>
                <h2 className="mt-4 mb-6 text-navy">Our <span className="text-blue-600">Referral Program.</span></h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
                  Know someone who would be a perfect fit for Leanport? Refer them and get rewarded! We're always looking for exceptional talent to join our remote-first team.
                </p>
                <div className="flex flex-wrap justify-center gap-6">
                  <div className="bg-blue-50 px-8 py-4 rounded-2xl">
                    <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">Referral Reward</p>
                    <p className="text-2xl font-black text-navy">$1,500+</p>
                  </div>
                  <Link to="/contact" className="btn-primary">
                    Refer a Friend
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section id="culture" className="shell-white border-t border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Our Values</span>
              <h2 className="mt-4 mb-6 text-navy">Principles that <span className="text-blue-600">drive Leanport.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We aren't just building a tool; we're building a culture of excellence and trust.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, idx) => (
                <div key={idx} className="group p-8 bg-white border border-gray-100 rounded-[32px] hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all reveal stagger-1">
                  <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                    <value.icon className={`w-8 h-8 ${value.iconColor}`} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="shell-white py-24 border-t border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Perks & Benefits</span>
                <h2 className="mt-4 mb-8 text-navy">Work that works <span className="text-blue-600">for your life.</span></h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4 text-blue-600">
                        <benefit.icon className="w-5 h-5" />
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{benefit.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reveal-right">
                <div className="relative rounded-[40px] overflow-hidden shadow-2xl">
                  <div className="bg-navy p-12 text-white">
                    <h3 className="text-3xl font-bold mb-8">Why PMS?</h3>
                    <ul className="space-y-6">
                      {[
                        "We use our own tool to build the tool.",
                        "Direct impact on thousands of teams.",
                        "Remote-first, async-friendly workflow.",
                        "Committed to open source and security."
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-4">
                          <CheckCircle2 className="w-6 h-6 text-sky flex-shrink-0" />
                          <span className="text-lg text-blue-100">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hiring Process */}
        <section className="shell-white section-narrow border-t border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Our Process</span>
              <h2 className="mt-4 mb-6 text-navy">How we <span className="text-blue-600">hire.</span></h2>
              <p className="text-gray-600">Transparent and efficient, just like our product.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {hiringSteps.map((step, idx) => (
                <div key={idx} className="relative reveal stagger-1">
                  {idx < hiringSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-80px)] h-0.5 bg-gray-100" />
                  )}
                  <div className="text-center">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white shadow-lg">
                      <step.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">Step {idx + 1}</div>
                    <h3 className="text-xl font-bold mb-3 text-navy">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="shell-white py-24 border-t border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Grid Container */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* LEFT SIDE (Small Space) */}
              <div className="lg:col-span-5">
                <div className="reveal">
                  <span className="section-eyebrow">FAQ</span>
                  <h2 className="mt-4 mb-6 stagger-1">
                    Candidate <span className="text-blue-600">FAQs.</span>
                  </h2>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    Get answers to common questions about the application process,
                    interviews, hiring timeline, and more.
                  </p>
                </div>
              </div>

              {/* RIGHT SIDE (Large Space) */}
              <div className="lg:col-span-7 space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 transition-all hover:border-blue-200 reveal stagger-1"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full px-8 py-6 text-left flex items-center justify-between group"
                    >
                      <span className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {faq.q}
                      </span>
                
                      {openFaq === idx ? (
                        <ChevronUp className="text-blue-600" />
                      ) : (
                        <ChevronDown className="text-gray-400" />
                      )}
                    </button>
                    
                    {openFaq === idx && (
                      <div className="px-8 pb-6 text-gray-600 leading-relaxed animate-fadeIn">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
            </div>
          </div>
        </section>


        {/* Open Positions List */}
        <section id="positions" className="shell-sky py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 reveal">
              <span className="section-eyebrow">Current Openings</span>
              <h2 className="mt-4 mb-6 text-navy">Join the <span className="text-blue-600">mission.</span></h2>
            </div>
            
            {/* <div className="space-y-4">
              {openPositions.map((job, idx) => (
                <div key={idx} className="group p-8 bg-white border border-gray-100 rounded-3xl hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/5 transition-all flex flex-col md:flex-row items-center justify-between gap-6 reveal stagger-1">
                  <div className="flex items-center gap-6 text-center md:text-left w-full">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all transform group-hover:scale-105">
                      <job.icon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                        <h3 className="text-2xl font-bold text-gray-900">{job.title}</h3>
                        {job.tag && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full uppercase tracking-widest">
                            {job.tag}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-6 justify-center md:justify-start">
                        <span className="flex items-center gap-2 text-gray-500 font-medium">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-2 text-gray-500 font-medium">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-2 text-gray-500 font-medium">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary !py-4 px-8 whitespace-nowrap group-hover:shadow-blue-500/20">
                    Apply Now
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              ))}
            </div> */}

            <div className="mt-10 p-12 bg-navy rounded-[40px] text-center reveal shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-5">
                <Rocket className="w-40 h-40 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-white">Don't see your role?</h3>
              <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
                We're always looking for exceptional talent. If you're passionate about what we're building, we want to hear from you.
              </p>
              <Link to="/contact" className="btn-secondary !bg-sky !text-navy !border-sky">
                Send Open Application
              </Link>
            </div>
          </div>
        </section>

        {/* Trust & Testimonial */}
        <section className="shell-white section-narrow border-t border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal">
            <div className="flex justify-center mb-8">
              <div className="flex -space-x-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`w-14 h-14 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500`}>
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-2xl font-medium text-gray-900 mb-6 italic">
              "Building PMS at Leanport is the most rewarding technical challenge of my career. The culture of trust and autonomy is real."
            </p>
            <div className="text-gray-600">
              <span className="font-bold text-gray-900">Qasim</span> — Senior Developer at Leanport
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default CareersPage;
